/**
 * <SEO /> — declarative per-page SEO tags.
 *
 * Uses react-helmet-async so the tags are captured at BUILD time by the
 * prerender pipeline (@prerenderer/prerenderer + Puppeteer) and land in
 * the static HTML that Googlebot sees. At runtime, Helmet keeps mutating
 * the same <head> tags so client-side navigation still updates titles /
 * OG cards without a page reload.
 *
 * The API mirrors the legacy `setSEO()` helper (lib/seo.js) so migrating
 * a page is a mechanical rename.
 */
import { Helmet } from "react-helmet-async";

const SITE = "https://discoveur.com";
const DEFAULT_IMAGE = "https://discoveur.com/icon-512.png";
const DEFAULT_TITLE = "Discoveur – La plateforme collaborative des sorties et activités des Outre-mer";
const DEFAULT_DESCRIPTION = "Trouvez quoi faire en Martinique, Guadeloupe, Guyane, Mayotte, La Réunion, Nouvelle-Calédonie, Polynésie française, Saint-Barthélémy, Saint-Martin et toutes les Outre-mer.";

export default function SEO({
    title,
    description,
    canonical,
    image,
    type = "website",
    jsonLd,
} = {}) {
    const finalTitle = title || DEFAULT_TITLE;
    const finalDescription = description || DEFAULT_DESCRIPTION;
    const finalCanonical = canonical || SITE;
    const finalImage = image || DEFAULT_IMAGE;

    // jsonLd may be a single object or an array — normalise to an array
    // so each block gets its own <script> tag (Google recommends this
    // over merging BreadcrumbList + Article/Event into one blob).
    const jsonLdArr = jsonLd
        ? (Array.isArray(jsonLd) ? jsonLd.filter(Boolean) : [jsonLd])
        : [];

    return (
        <Helmet prioritizeSeoTags>
            <title>{finalTitle}</title>
            <meta name="description" content={finalDescription} />
            <link rel="canonical" href={finalCanonical} />

            <meta property="og:title" content={finalTitle} />
            <meta property="og:description" content={finalDescription} />
            <meta property="og:url" content={finalCanonical} />
            <meta property="og:image" content={finalImage} />
            <meta property="og:type" content={type} />

            <meta name="twitter:title" content={finalTitle} />
            <meta name="twitter:description" content={finalDescription} />
            <meta name="twitter:image" content={finalImage} />

            {jsonLdArr.map((obj, i) => (
                <script key={`${obj?.["@type"] || "jsonld"}-${i}`} type="application/ld+json">
                    {JSON.stringify(obj)}
                </script>
            ))}
        </Helmet>
    );
}
